import { useState, useRef } from 'react'
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Typography,
  Stack,
  TextField,
  CircularProgress,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material'
import { CloudUpload as UploadIcon } from '@mui/icons-material'
import type { POImportMode } from '@/types/po'
import { useT } from '@/i18n/useT'

interface POImportDialogProps {
  open: boolean
  defaultYear: number
  loading?: boolean
  error?: string | null
  onClose: () => void
  onImport: (file: File, year: number, mode: POImportMode) => void
}

export function POImportDialog({
  open,
  defaultYear,
  loading = false,
  error = null,
  onClose,
  onImport,
}: POImportDialogProps) {
  const { t } = useT()
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [year, setYear] = useState<string>(String(defaultYear))
  const [mode, setMode] = useState<POImportMode>('update')
  const [localError, setLocalError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const validExtensions = ['.xlsx', '.xls', '.csv']
    const fileExtension = '.' + (file.name.split('.').pop()?.toLowerCase() ?? '')
    if (!validExtensions.includes(fileExtension)) {
      setLocalError(t('Please select a valid Excel (.xlsx, .xls) or CSV file'))
      setSelectedFile(null)
      return
    }
    setSelectedFile(file)
    setLocalError(null)
  }

  const handleImport = () => {
    const parsedYear = Number(year)
    if (!Number.isInteger(parsedYear) || parsedYear < 2000 || parsedYear > 2100) {
      setLocalError(t('Please enter a valid year (2000–2100)'))
      return
    }
    if (!selectedFile) {
      setLocalError(t('Please select a file'))
      return
    }
    setLocalError(null)
    onImport(selectedFile, parsedYear, mode)
  }

  const handleClose = () => {
    if (loading) return
    setSelectedFile(null)
    setLocalError(null)
    setYear(String(defaultYear))
    setMode('update')
    if (fileInputRef.current) fileInputRef.current.value = ''
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('Import PO list')}</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ pt: 2 }}>
          <TextField
            label={t('Year')}
            type="number"
            value={year}
            onChange={(e) => setYear(e.target.value)}
            fullWidth
            helperText={t('POs will be imported for this year')}
          />

          <FormControl>
            <FormLabel>{t('Import mode')}</FormLabel>
            <RadioGroup value={mode} onChange={(e) => setMode(e.target.value as POImportMode)}>
              <FormControlLabel
                value="update"
                control={<Radio />}
                label={t('Update / add (match by PO number, keep invoice assignments)')}
              />
              <FormControlLabel
                value="replace"
                control={<Radio />}
                label={t('Replace the whole year')}
              />
            </RadioGroup>
          </FormControl>

          <Box
            sx={{
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="body1" gutterBottom>
              {t('Click to select a PO list file')}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {/* Literal headers of the source workbook — data, not UI */}
              {t('Expected columns:')} PO Number, Pivot PO Number, Supplier Name, PO Description,
              PO Currency, Unit Price Total
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </Box>

          {selectedFile && (
            <Alert severity="info">
              {t('Selected:')} <strong>{selectedFile.name}</strong> (
              {(selectedFile.size / 1024).toFixed(2)} KB)
            </Alert>
          )}

          {(localError || error) && <Alert severity="error">{localError || error}</Alert>}

          {mode === 'replace' ? (
            <Alert severity="warning">
              <Typography variant="body2">
                <strong>{t('Warning:')}</strong>{' '}
                {t(
                  'This replaces all existing POs for the selected year. Linked invoices are unassigned (not deleted).',
                )}
              </Typography>
            </Alert>
          ) : (
            <Alert severity="info">
              <Typography variant="body2">
                {t(
                  'Existing POs (matched by PO number) are updated in place and keep their linked invoices. New POs are added; other POs of the year are left untouched.',
                )}
              </Typography>
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {t('Cancel')}
        </Button>
        <Button
          onClick={handleImport}
          variant="contained"
          disabled={!selectedFile || loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {loading ? t('Importing …') : t('Import')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
